/**
 * Utility module for working with browser storage (localStorage/sessionStorage).
 * @module browserStorageUtils
 */

/**
 * A helper function to determine if a string is valid JSON.
 *
 * @param {string} str - The string to check.
 * @returns {boolean} Returns true if the string is valid JSON, otherwise false.
 */
const isJsonString = (str) => {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

/**
 * A function to store data in the specified storage (localStorage/sessionStorage).
 * Automatically stringifies non-primitive values (objects/arrays).
 *
 * @param {string} storageType - Type of storage ('localStorage' or 'sessionStorage').
 * @param {string} key - The key under which to store the value.
 * @param {any} value - The value to store in the storage.
 */
const setStorage = (storageType, key, value) => {
  try {
    if (!['localStorage', 'sessionStorage'].includes(storageType)) {
      console.error(
        `Invalid storage type: ${storageType}. Please use 'localStorage' or 'sessionStorage'.`,
      )
      return
    }

    const valueToStore =
      typeof value === 'object' ? JSON.stringify(value) : value
    window[storageType].setItem(key, valueToStore)
  } catch (error) {
    console.error(`Error storing data in ${storageType}:`, error)
  }
}

/**
 * A function to retrieve data from the specified storage (localStorage/sessionStorage).
 * Automatically parses the data back from JSON if needed.
 * Returns a default value (null) if the data cannot be retrieved.
 *
 * @param {string} storageType - Type of storage ('localStorage' or 'sessionStorage').
 * @param {string} key - The key under which the value is stored.
 * @returns {any} The value retrieved from storage, or a default value (null) if not found.
 */
const getStorage = (storageType, key) => {
  try {
    if (!['localStorage', 'sessionStorage'].includes(storageType)) {
      console.error(
        `Invalid storage type: ${storageType}. Please use 'localStorage' or 'sessionStorage'.`,
      )
      return null
    }

    const storedValue = window[storageType].getItem(key)

    // Return default value (null) if no value is found
    if (storedValue === null) return null

    // Return parsed value if it's valid JSON, else return the raw value
    return isJsonString(storedValue) ? JSON.parse(storedValue) : storedValue
  } catch (error) {
    console.error(`Error retrieving data from ${storageType}:`, error)
    return null // Default return value in case of an error
  }
}

/**
 * A function to clear the specified storage (localStorage/sessionStorage).
 *
 * @param {string} storageType - Type of storage ('localStorage' or 'sessionStorage').
 */
const clearStorage = (storageType) => {
  try {
    if (!['localStorage', 'sessionStorage'].includes(storageType)) {
      console.error(
        `Invalid storage type: ${storageType}. Please use 'localStorage' or 'sessionStorage'.`,
      )
      return
    }

    window[storageType].clear()
  } catch (error) {
    console.error(`Error clearing ${storageType}:`, error)
  }
}

/**
 * Sets an item in `localStorage`.
 * Automatically stringifies non-primitive values (objects/arrays).
 *
 * @param {string} key - The key under which to store the value.
 * @param {any} value - The value to store in localStorage.
 */
export const setLocalStorage = (key, value) =>
  setStorage('localStorage', key, value)

/**
 * Retrieves an item from `localStorage`.
 * Automatically parses the data back from JSON if needed.
 * Returns a default value (null) if retrieval fails or key is not found.
 *
 * @param {string} key - The key under which the value is stored.
 * @returns {any} The value retrieved from localStorage, or null if not found or error occurs.
 */
export const getLocalStorage = (key) => getStorage('localStorage', key)

/**
 * Clears all items from `localStorage`.
 */
export const clearLocalStorage = () => clearStorage('localStorage')
